import React, { useState, useEffect } from "react";
import { 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  Clock, 
  AlertTriangle, 
  ChevronDown, 
  ChevronUp,
  Activity
} from "lucide-react";
import axios from "axios";
import { API_BASE_URL, API_ENDPOINTS, REQUEST_CONFIG } from "../config";

/**
 * ApiTesting component displays API unit test results
 * @returns {JSX.Element}
 */
export default function ApiTesting() {
  const [testReport, setTestReport] = useState(null);
  const [testReportLoading, setTestReportLoading] = useState(false);
  const [expandedSuites, setExpandedSuites] = useState({});
  
  // Fetch test report when component mounts
  useEffect(() => {
    fetchTestReport();
  }, []);
  
  // Function to fetch the test report
  const fetchTestReport = async () => {
    try {
      setTestReportLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.API_UNIT_TESTS_REPORT}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      if (response.data && response.data.status === 'success') {
        // Parse XML content from the response
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(response.data.report, "text/xml");
        
        // Store both the raw XML and the parsed document
        setTestReport({
          ...response.data,
          parsedXml: xmlDoc
        });
      } else {
        setTestReport({
          status: 'error',
          message: response.data.message || 'Failed to fetch test report'
        });
      }
      setTestReportLoading(false);
    } catch (error) {
      setTestReport({
        status: 'error',
        message: error.message || 'An error occurred'
      });
      setTestReportLoading(false);
    }
  };
  
  // Toggle expanded state for a test suite
  const toggleSuite = (suiteId) => {
    setExpandedSuites(prev => ({
      ...prev,
      [suiteId]: !prev[suiteId]
    }));
  };
  
  // Parse test stats from XML document
  const parseTestStats = (xmlDoc) => {
    if (!xmlDoc) return { total: 0, failures: 0, passed: 0, time: 0 };
    
    try {
      const testsuites = xmlDoc.getElementsByTagName('testsuites')[0];
      if (!testsuites) return { total: 0, failures: 0, passed: 0, time: 0 };
      
      const total = parseInt(testsuites.getAttribute('tests') || '0', 10);
      const failures = parseInt(testsuites.getAttribute('failures') || '0', 10);
      const errors = parseInt(testsuites.getAttribute('errors') || '0', 10);
      const time = parseFloat(testsuites.getAttribute('time') || '0');
      
      return {
        total,
        failures: failures + errors,
        passed: total - (failures + errors),
        time
      };
    } catch (error) {
      console.error('Error parsing test stats:', error);
      return { total: 0, failures: 0, passed: 0, time: 0 };
    }
  };
  
  // Get individual test suites
  const getTestSuites = (xmlDoc) => {
    if (!xmlDoc) return [];
    
    try {
      const suites = xmlDoc.getElementsByTagName('testsuite');
      return Array.from(suites).map((suite, index) => {
        const suiteName = suite.getAttribute('name') || `Suite ${index}`;
        const tests = parseInt(suite.getAttribute('tests') || '0', 10);
        const failures = parseInt(suite.getAttribute('failures') || '0', 10);
        const time = parseFloat(suite.getAttribute('time') || '0');
        const timestamp = suite.getAttribute('timestamp') || '';
        
        // Get properties
        const properties = Array.from(suite.getElementsByTagName('property')).map(prop => ({
          name: prop.getAttribute('name'),
          value: prop.getAttribute('value')
        }));
        
        // Get testcases
        const testcases = Array.from(suite.getElementsByTagName('testcase')).map(testcase => ({
          classname: testcase.getAttribute('classname'),
          name: testcase.getAttribute('name'),
          time: parseFloat(testcase.getAttribute('time') || '0'),
          failures: Array.from(testcase.getElementsByTagName('failure')).map(failure => ({
            message: failure.getAttribute('message'),
            type: failure.getAttribute('type'),
            content: failure.textContent
          })),
          errors: Array.from(testcase.getElementsByTagName('error')).map(error => ({
            message: error.getAttribute('message'),
            type: error.getAttribute('type'),
            content: error.textContent
          }))
        }));
        
        return {
          id: `suite-${index}`,
          name: suiteName,
          tests,
          failures,
          passed: tests - failures,
          time,
          timestamp,
          properties,
          testcases
        };
      });
    } catch (error) {
      console.error('Error parsing test suites:', error);
      return [];
    }
  };
  
  // Get test stats if report is available
  const testStats = testReport?.parsedXml ? parseTestStats(testReport.parsedXml) : { total: 0, failures: 0, passed: 0, time: 0 };
  const testSuites = testReport?.parsedXml ? getTestSuites(testReport.parsedXml) : [];
  
  // Format test case name to be more readable
  const formatTestName = (name) => {
    return name
      .replace(/_/g, ' ')
      .replace(/\//g, ' - ')
      .replace(/\\'s/g, "'s")
      .replace(/\\/g, '');
  };
  
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">API Testing Results</h1>
        <button 
          onClick={fetchTestReport} 
          disabled={testReportLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center"
        >
          <RefreshCw size={16} className={`mr-2 ${testReportLoading ? "animate-spin" : ""}`} />
          {testReportLoading ? "Refreshing..." : "Refresh Tests"}
        </button>
      </div>
      
      {/* Test Summary Card */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
          <h2 className="text-xl font-medium text-gray-800 mb-4">Test Summary</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-800">{testStats.total}</div>
              <div className="text-sm text-gray-500">Total Tests</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{testStats.passed}</div>
              <div className="text-sm text-green-600">Passed</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{testStats.failures}</div>
              <div className="text-sm text-red-600">Failed</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{testStats.time.toFixed(2)}s</div>
              <div className="text-sm text-blue-600">Duration</div>
            </div>
          </div>
          
          {testReport?.timestamp && (
            <div className="flex items-center text-xs text-gray-500 mt-4">
              <Clock size={12} className="mr-1" />
              Last run: {new Date(testReport.timestamp).toLocaleString()}
            </div>
          )}
        </div>
      </div>
      
      {/* Test Results Card */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-50 text-green-600 mr-4">
              <Activity size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Test Suites</h2>
              <p className="text-sm text-gray-500">API Unit Test Results</p>
            </div>
          </div>
          <div className="flex items-center">
            {testStats.failures > 0 ? (
              <span className="bg-red-100 text-red-700 text-xs font-medium px-2.5 py-0.5 rounded-full">
                {testStats.failures} Failing
              </span>
            ) : (
              <span className="bg-green-100 text-green-700 text-xs font-medium px-2.5 py-0.5 rounded-full">
                All Passing
              </span>
            )}
          </div>
        </div>
        
        <div className="p-6">
          {testReportLoading ? (
            <div className="flex justify-center py-20">
              <div className="animate-pulse flex flex-col items-center">
                <div className="rounded-full bg-gray-200 w-16 h-16 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-32 mb-3"></div>
                <div className="h-3 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
          ) : testReport?.status === 'error' ? (
            <div className="bg-red-50 border border-red-100 rounded-lg p-6 text-red-700 text-center">
              <AlertTriangle size={40} className="mx-auto mb-4" />
              <h3 className="font-medium text-lg mb-2">Error retrieving test report</h3>
              <p className="text-sm">{testReport.message || 'Failed to load test data. Please try refreshing the report.'}</p>
              <button 
                onClick={fetchTestReport}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Try Again
              </button>
            </div>
          ) : testReport?.status === 'pending' ? (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 text-blue-700 text-center">
              <Clock size={40} className="mx-auto mb-4" />
              <h3 className="font-medium text-lg mb-2">Tests are running</h3>
              <p className="text-sm">The test suite is still running. Please check back later.</p>
              <div className="mt-4 w-full h-2 bg-blue-200 rounded-full overflow-hidden">
                <div className="h-2 rounded-full bg-blue-600 animate-pulse" style={{ width: '60%' }}></div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {testSuites.length === 0 ? (
                <p className="text-gray-700 text-center py-8">No test suites found in the report.</p>
              ) : (
                testSuites.map(suite => {
                  const isExpanded = expandedSuites[suite.id] || false;
                  const hasFailures = suite.failures > 0;
                  const hasTests = suite.tests > 0;
                  
                  // Skip suites with 0 tests unless explicitly expanded
                  if (suite.tests === 0 && !isExpanded) return null;
                  
                  return (
                    <div 
                      key={suite.id} 
                      className={`border rounded-lg ${hasFailures ? 'border-red-200' : 'border-gray-200'}`}
                    >
                      <div 
                        className={`px-4 py-3 flex items-center justify-between cursor-pointer ${hasFailures ? 'bg-red-50' : 'bg-gray-50'}`}
                        onClick={() => toggleSuite(suite.id)}
                      >
                        <div className="flex items-center">
                          {hasFailures ? (
                            <XCircle size={18} className="text-red-600 mr-2" />
                          ) : (
                            <CheckCircle size={18} className="text-green-600 mr-2" />
                          )}
                          <span className="font-medium">{suite.name}</span>
                          {hasTests && (
                            <>
                              <span className="text-gray-500 text-sm ml-4">
                                {suite.tests} {suite.tests === 1 ? 'test' : 'tests'}
                              </span>
                              <span className={`text-sm ml-3 ${hasFailures ? 'text-red-600' : 'text-green-600'}`}>
                                {hasFailures ? `${suite.failures} failed` : 'all passing'}
                              </span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-500 text-sm mr-2">{suite.time.toFixed(3)}s</span>
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div className="p-4 border-t border-gray-200">
                          {suite.properties.length > 0 && (
                            <div className="mb-4">
                              <div className="font-medium text-sm text-gray-600 mb-1">Properties</div>
                              <div className="bg-gray-50 p-2 rounded text-sm">
                                {suite.properties.map((prop, idx) => (
                                  <div key={idx} className="mb-1">
                                    <span className="font-medium">{prop.name}:</span> {prop.value}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {suite.testcases.length > 0 ? (
                            <div>
                              <div className="font-medium text-sm text-gray-600 mb-2">Test Cases</div>
                              <div className="space-y-2">
                                {suite.testcases.map((testcase, idx) => {
                                  const hasFailuresOrErrors = testcase.failures.length > 0 || testcase.errors.length > 0;
                                  
                                  return (
                                    <div 
                                      key={idx} 
                                      className={`p-2 rounded ${hasFailuresOrErrors ? 'bg-red-50' : 'bg-gray-50'}`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                          {hasFailuresOrErrors ? (
                                            <XCircle size={14} className="text-red-600 mr-2 flex-shrink-0" />
                                          ) : (
                                            <CheckCircle size={14} className="text-green-600 mr-2 flex-shrink-0" />
                                          )}
                                          <span className="text-sm">{formatTestName(testcase.name)}</span>
                                        </div>
                                        <span className="text-gray-500 text-xs">{testcase.time.toFixed(6)}s</span>
                                      </div>
                                      
                                      {hasFailuresOrErrors && (
                                        <div className="mt-2 ml-6 text-xs text-red-700">
                                          {testcase.failures.map((failure, fidx) => (
                                            <div key={`failure-${fidx}`} className="mb-1">
                                              <div className="font-medium">Failure: {failure.message || failure.type}</div>
                                              {failure.content && (
                                                <pre className="mt-1 p-1 bg-red-100 rounded overflow-x-auto">
                                                  {failure.content}
                                                </pre>
                                              )}
                                            </div>
                                          ))}
                                          
                                          {testcase.errors.map((error, eidx) => (
                                            <div key={`error-${eidx}`} className="mb-1">
                                              <div className="font-medium">Error: {error.message || error.type}</div>
                                              {error.content && (
                                                <pre className="mt-1 p-1 bg-red-100 rounded overflow-x-auto">
                                                  {error.content}
                                                </pre>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <p className="text-gray-500 text-sm">No test cases found in this suite.</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
